import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { merchant as mApi } from './api';
import { useFetch } from './hooks';

const FlContext = createContext(null);
const KEY = 'fl.activeMerchant';

/**
 * Who the signed-in user is in 3PL: their merchant organisations, staff
 * roles and which workspaces they may open. The active merchant is
 * remembered per browser; the server checks membership on every call.
 */
export function FlProvider({ children }) {
  const me = useFetch(() => mApi.me(), []);
  const [chosen, setChosen] = useState(() => {
    try { return localStorage.getItem(KEY) || ''; } catch { return ''; }
  });
  const merchants = useMemo(() => me.data?.merchants || [], [me.data]);
  const active = merchants.find((m) => m.merchant_id === chosen) || merchants[0] || null;
  const choose = useCallback((id) => {
    setChosen(id);
    try { localStorage.setItem(KEY, id); } catch { /* ignore */ }
  }, []);
  const value = useMemo(() => ({
    me, merchants, merchant: active, merchantId: active?.merchant_id || '', role: active?.role || '',
    chooseMerchant: choose, staffRoles: me.data?.staff_roles || [], workspaces: me.data?.workspaces || {},
    can: (perm) => {
      const r = active?.role;
      const map = {
        catalog: ['owner', 'staff'], inbound: ['owner', 'staff'], orders: ['owner', 'staff'],
        returns: ['owner', 'staff'], billing: ['owner', 'finance'], team: ['owner'], settings: ['owner'],
      };
      return !!r && (map[perm] || ['owner', 'staff', 'finance']).includes(r);
    },
    staffCan: (perm) => {
      const s = new Set(me.data?.staff_roles || []);
      const map = {
        floor: ['operator', 'supervisor', 'ops_admin'], supervise: ['supervisor', 'ops_admin'],
        ops: ['ops_admin'], finance: ['finance'],
      };
      return (map[perm] || []).some((x) => s.has(x));
    },
  }), [me, merchants, active, choose]);
  return <FlContext.Provider value={value}>{children}</FlContext.Provider>;
}

export function useFl() {
  const ctx = useContext(FlContext);
  if (!ctx) throw new Error('useFl must be used inside FlProvider');
  return ctx;
}
